import differenceInHours from 'date-fns/differenceInHours';
import { rateLimitingEnabled } from '@/utils';
import { includedInPaidPurchaseOrders } from '@/utils/acl';

export default {
  computed: {
    creditLimitReached() {
      if (!this.rateLimitingEnabled || this.hasActivePaidSubscription) {
        return false;
      }
      return this.questionsRemaining <= 0;
    },
    hasActivePaidSubscription() {
      return this.$store.getters['purchaseReceipts/active'].some(pr => {
        const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
        return po && !po.isFree && ['Subscription', 'Tiered Pricing'].includes(po.purchaseOrderType);
      });
    },
    questionsRemaining() {
      return this.$store.getters['user/rateLimitCredits'];
    },
    rateLimitingEnabled() {
      return rateLimitingEnabled({
        apptimize: this.$apptimize,
        store: this.$store,
        analytics: this.$analytics,
      });
    },
    rateLimitingCycleCredits() {
      return this.$store.getters['app/rateLimitingCycleCredits'];
    },
    rateLimitCycleDays() {
      const appValue = this.$store.getters['app/rateLimitingCycleDay'];

      const apptimizeValue = this.$apptimize.checkExperiments({
        experimentName: 'Rate Limit Cycle Day',
        int: 'rateLimitCycleDay',
        defaultVal: 0,
        matcher: /Rate Limit Cycle Day/,
      });
      if (!isNaN(Number(apptimizeValue)) && Number(apptimizeValue) > 0) {
        return apptimizeValue;
      }

      return appValue;
    },
    shouldShowRateLimit() {
      if (!this.rateLimitingEnabled) {
        return false;
      }

      // Check for addon purchase order logic (only if component defines isAddonPurchaseOrder)
      if ('isAddonPurchaseOrder' in this && this.isAddonPurchaseOrder) {
        const hasPurchasedAddon = this.$store.getters['purchaseReceipts/active'].some(pr => {
          const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
          return po.parentId == this.jsonConfig?.purchaseOrderId && !po.isFree;
        });
        return !hasPurchasedAddon;
      }

      let shouldShow = false;
      try {
        // A dashboard-like context arrives as a `dashboard` prop (rate-limit widget)
        // or a `source` prop (counter banner); we only read its `unlockedBy` ids.
        // (`source` is overloaded elsewhere for analytics objects, hence the generic
        // name and the `unlockedBy` guard below.) The global counter button has
        // neither prop, so guard with `in this` to avoid reading an undeclared
        // instance property.
        const dashboardContext = ('dashboard' in this && this.dashboard)
          || ('source' in this && this.source)
          || null;
        if (this?.$route?.name == 'dashboard#group' && dashboardContext?.unlockedBy) {
          // This user is seeing this particular widget within the dashboard-groups context.
          // This context only exists within the catalog context. Because of this the decision to show this widget
          // should be based on the user owning the none free (paid) add on for this particular dashboard.
          const dashboardLocked = this.$store.getters['purchaseReceipts/active'].some( pr => {
            const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
            const poId = dashboardContext.unlockedBy.find( id => parseInt(id) == parseInt(pr.purchaseOrderId));
            return !po.isFree && poId;
          });
          shouldShow = !dashboardLocked;
        } else {
          // This user is seeing this widget on the main dashboard context which is the home context to the user
          // If they should see this widget should be based on them owning the main subscription which is the
          // purchase_order of type 'Subscription' or 'Tiered Pricing'
          const hasPaidReceipt = this.$store.getters['purchaseReceipts/active'].some(pr => {
            const po = this.$store.getters['purchaseOrders/get'][pr.purchaseOrderId];
            return !po.isFree && ['Subscription', 'Tiered Pricing'].includes(po.purchaseOrderType);
          });
          shouldShow = !hasPaidReceipt;
        }

        return shouldShow;
      } catch {
        return false;
      }
    },
    showBannerOrCircle() {
      if (this.user?.previewer) {
        return 'banner';
      }
      // if the user is rate limited, we should show the banner or circle but not both
      const includedInNewRateLimitCounterTest = this.$apptimize.checkExperiments({
        experimentName: 'Rate Limit Counter',
        bool: 'NewCounterDesign',
        defaultVal: false,
        matcher: /rate limit counter/gi,
      });

      return includedInNewRateLimitCounterTest ? 'banner' : 'circle';
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  methods: {
    checkRateLimitCredits() {
      if (this.rateLimitingEnabled) {
        const shouldAward = this.userNeedsRateLimitCredits();
        if (shouldAward) {
          this.$store.dispatch('user/awardRateLimitCredits', this.rateLimitingCycleCredits);
        }
      }
    },
    flashcardIsRateLimited(flashcard) {
      if (!flashcard?.categoryIds || !this.rateLimitingEnabled) {
        return false;
      }

      const hasPaidAccess = flashcard?.categoryIds.some(cid => {
        const category = this.$store.getters['categories/categories'][cid];
        if (!category) return false;
        return includedInPaidPurchaseOrders(this.$store, category);
      });

      return !hasPaidAccess;
    },
    userNeedsRateLimitCredits() {
      const now = new Date();
      const lastCreditAward = this.$store.getters['user/rateLimitCreditsResetAt'];
      if (lastCreditAward) {
        const hoursSinceAward = differenceInHours(now, lastCreditAward);
        return hoursSinceAward >= (Number(this.rateLimitCycleDays) || 1) * 24;
      }
      return true;
    },
  },
  mounted() {
    this.checkRateLimitCredits();
  },
};
